import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { formatRelativeTime, cn } from '@/lib/utils';

export function Notifications() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data } = await api.get('/notifications');
      return data;
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('notifications.title')}</h1>
        {data?.unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => markAllRead.mutate()} className="gap-2">
            <CheckCheck className="h-4 w-4" />
            {t('notifications.markAllRead')}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {!data?.notifications?.length ? (
            <div className="flex flex-col items-center py-16">
              <Bell className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="divide-y">
              {data.notifications.map((notif: any) => (
                <div
                  key={notif.id}
                  className={cn(
                    'flex items-start gap-4 p-4 hover:bg-accent/50 transition-colors',
                    !notif.isRead && 'bg-primary/5',
                  )}
                >
                  <div className={cn('mt-1 h-2 w-2 rounded-full flex-shrink-0', notif.isRead ? 'bg-transparent' : 'bg-primary')} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{notif.title}</p>
                    <p className="text-sm text-muted-foreground">{notif.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatRelativeTime(notif.createdAt || notif.created_at)}</p>
                  </div>
                  {!notif.isRead && (
                    <button onClick={() => markRead.mutate(notif.id)} className="p-1 text-muted-foreground hover:text-primary">
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
